import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { numberFormat, setDocumentTitle } from 'shared/util'

import { mergeBoundary } from 'shared/location'
import { SearchIcon } from 'shared/icons'

import Leaflet from 'leaflet'

import {
  Paper,
  PaperProps,
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Alert
} from '@mui/material'

import { MapIcon } from 'shared/icons'
import { FormatListNumbered as ListIcon } from '@mui/icons-material'

import { PrefectureResult } from 'interface/prefecture'

import { useSideMenu } from 'components/sideMenu/container'
import { useTopBar } from 'components/topBar/container'
import { useTenant } from 'features/tenant/context'

import { Loading } from 'components/controls/loading'
import { ErrorInfo } from 'components/controls/errorInfo'
import { ValueSpan } from 'components/controls/valueSpan'
import { ColumnSplitter } from 'components/controls/splitter'
import { SearchWordField } from 'components/controls/searchWordField'
import { AreaBar } from 'components/areaBar'

import { DpcResultTable } from './components/resultTable'
import { FacilityMapView } from './components/mapView'
import { FindDiseasePopover } from './components/findDiseasePopover'

import { useDpcResults } from './container'
import { useDpcResultDownload } from './container/useDownload'
import { DpcDiseaseList } from './components/dpcDiseaseList'
import { DpcPrefectureTable } from './components/prefectureTable'

import { SisetuPreviewDialog } from 'features/sisetu/dialog/preview'
import { useSisetuPreviewDialog } from 'features/sisetu/dialog/preview/hooks'

const FacilityTabPanel = ({
  currentTab,
  tabValue,
  children,
}: {
  currentTab: string
  tabValue: string
  children: ReactNode
}) => {
  return (
    <Box
      role="tabpanel"
      hidden={currentTab !== tabValue}
      id={`facility-tabpanel-${currentTab}`}
      aria-labelledby={`facility-tabpanel-${currentTab}`}
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {children}
    </Box>
  )
}

type DpcResultsPageProps = {
  topBarState: ReturnType<typeof useTopBar>
  sideMenuState: ReturnType<typeof useSideMenu>
} & PaperProps
export const DpcResultsPage = ({
	topBarState: { setBreadCrumbs },
	sideMenuState: { setActivePage },
	...others
}: DpcResultsPageProps) => {
  const tenant = useTenant()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const container = useDpcResults()
  const {
    loading,
    errors,
    mdcList,
    tabValue, setTabValue,
    selectedMdcCode, selectMdcCode,
    selectedDpcCodes,
    selectedDpcList,
    clearSelectedDpcCode,
    block, setBlock,
    prefectures, setPrefectures,
    filterWord, setFilterWord,
    calcTotalCase,
    results,
  } = container

  const {
		sisetuList,
		sisetuCode,
		setSisetuList,
		openSisetu,
		close: closeSisetuDialog
  } = useSisetuPreviewDialog()

  const onSisetuClick = useCallback(
    (sisetuCode: string | null) => {
      setSisetuList(results?.map(r => r.sisetu) ?? [])
      openSisetu(sisetuCode)
    },
    [openSisetu, results, setSisetuList]
  )

  const { doDownload } = useDpcResultDownload()
  const mapRef = useRef<Leaflet.Map | null>(null)

  const fitBoundsMap = useCallback(
    (prefectures: PrefectureResult[]) => {
      const map = mapRef.current
      if (map && prefectures.length > 0) {
        const boundary = mergeBoundary(
          prefectures.map((prefecture) => prefecture.boundary)
        )
        map.fitBounds([
          [boundary.s, boundary.w],
          [boundary.n, boundary.e],
        ])
      }
    },
    [mapRef]
  )
  const onMapDragEnd = useCallback(
    () => {
      mapRef.current?.invalidateSize()
    },
    [mapRef]
  )

  const onSelectedPrefecture = useCallback(
    (prefectures: PrefectureResult[]) => {
      const codes = prefectures.map(
        prefecture => prefecture.prefectureCode
      )
      setPrefectures(codes)
      fitBoundsMap(prefectures)
    },
    [setPrefectures, fitBoundsMap]
  )

  useEffect(
    () => {
      mapRef.current?.fireEvent('load')
    },
    [mapRef, tabValue]
  )

  useEffect(
    () => {
      if (tabValue === 'map') {
        mapRef.current?.invalidateSize()
      }
    },
    [mapRef, tabValue]
  )

  const onDownload = useCallback(
    () => {
      if (!results || results.length < 1) return
      const mdc = mdcList.find(mdc => mdc.mdcCode === selectedMdcCode)
      if (!mdc) return
      doDownload(
        mdc.mdcName,
        results,
        selectedDpcList,
        calcTotalCase
      )
    },
    [mdcList, results, selectedMdcCode, selectedDpcList, calcTotalCase, doDownload]
  )

  useEffect(
    () => {
      setBreadCrumbs([{ label: '施設実績検索' }])
      setDocumentTitle('施設実績検索', tenant.tenantName)
      setActivePage('dpcResult')
    },
    [tenant, setBreadCrumbs, setActivePage]
  )

  return errors.length > 0 ? (
    <Stack spacing={1}>
      {errors.map((error, i) => (
        <ErrorInfo key={`dpcresults-error-${i}`} error={error} />
      ))}
    </Stack>
  ) : (
    <Paper
      square
      sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative' }}
      {...others}
    >
      <Stack direction="row" alignItems="stretch" height="100%">
        {loading && <Loading />}
        <AreaBar
          defaultBlockCode={block}
          defaultPrefectureCodes={prefectures}
          onSelectedBlock={setBlock}
          onSelectedPrefecture={onSelectedPrefecture}
        />
        <ColumnSplitter onDragEnd={onMapDragEnd}>
          <Stack
            direction="column"
            position="relative"
            width="100%"
            height="100%"
            overflow="hidden"
          >
            <Stack
              padding={1}
              direction="row"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <FormControl fullWidth>
                <Select
                  variant="outlined"
                  size="small"
                  title="疾患領域"
                  value={selectedMdcCode}
                  onChange={(event) => selectMdcCode(event.target.value)}
                >
                  {mdcList.map(mdc => (
                    <MenuItem
                      key={`mdc-${mdc.mdcCode}`}
                      value={mdc.mdcCode}
                    >
                      {mdc.mdcName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                startIcon={<SearchIcon/>}
                sx={{
                  marginLeft: 1,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                疾患名で検索
              </Button>

              {anchorEl && (
                <FindDiseasePopover
                  anchorEl={anchorEl}
                  onSelectMdcCode={(mdcCode) => {
                    selectMdcCode(mdcCode)
                    setAnchorEl(null)
                  }}
                  onClose={() => setAnchorEl(null)}
                />
              )}
            </Stack>

            <DpcDiseaseList flexGrow={1} container={container}/>

            <Stack
              direction="row"
              justifyContent="space-between"
              padding={1}
              sx={{ borderTop: 1, borderColor: 'divider' }}
            >
              <Button
                variant="contained"
                disabled={selectedDpcCodes.length === 0}
                onClick={clearSelectedDpcCode}
              >
                選択をクリア
              </Button>
            </Stack>
          </Stack>

          <Stack overflow="hidden" width="100%" height="100%">
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              paddingX={1}
              sx={{
                borderBottom: 1,
                borderColor: 'divider'
              }}
            >
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                sx={{
                  flexGrow: 1,
                  height: '100%',
                  "& .MuiTabs-list": {
                    height: '100%'
                  }
                }}
              >
                <Tab
                  value="list"
                  label="施設実績一覧"
                  iconPosition="start"
                  icon={<ListIcon />}
                  sx={{ minHeight: 'auto' }}
                />
                <Tab
                  value="prefecture"
                  label="都道府県別"
                  iconPosition="start"
                  icon={<MapIcon />}
                  sx={{ minHeight: 'auto' }}
                />
                <Tab
                  value="map"
                  label="地図表示"
                  iconPosition="start"
                  icon={<MapIcon />}
                  sx={{ minHeight: 'auto' }}
                />
              </Tabs>

              <Box paddingY={1}>
                <SearchWordField
                  id="search-dpc-result"
                  searchWord={filterWord}
                  onChangeSearchWord={setFilterWord}
                />
              </Box>
            </Stack>

            <Box
              flexGrow={1}
              position="relative"
              overflow="hidden"
              height="100%"
            >
              <FacilityTabPanel currentTab="list" tabValue={tabValue}>
                {(prefectures.length === 0) ? (
                  <Box padding={1}>
                    <Alert severity="info">都道府県を選択して下さい</Alert>
                  </Box>
                ) : (selectedDpcCodes.length === 0) ? (
                  <Box padding={1}>
                    <Alert severity="info">疾患を選択して下さい</Alert>
                  </Box>
                ) : !results ? (
                  <></>
                ) : (results.length === 0) ? (
                  <Box padding={1}>
                    <Alert severity="info">該当する施設が見つかりません.</Alert>
                  </Box>
                ) : (
                  <DpcResultTable
                    results={results}
                    selectedDpcList={selectedDpcList}
                    calcTotalCase={calcTotalCase}
                    onSisetuClick={onSisetuClick}
                  />
                )}
              </FacilityTabPanel>

              <FacilityTabPanel currentTab="prefecture" tabValue={tabValue}>
                {(prefectures.length === 0) ? (
                  <Box padding={1}>
                    <Alert severity="info">都道府県を選択して下さい</Alert>
                  </Box>
                ) : (selectedDpcCodes.length === 0) ? (
                  <Box padding={1}>
                    <Alert severity="info">疾患を選択して下さい</Alert>
                  </Box>
                ) : !results ? (
                  <></>
                ) : (results.length === 0) ? (
                  <Box padding={1}>
                    <Alert severity="info">該当する施設が見つかりません.</Alert>
                  </Box>
                ) : (
                  <DpcPrefectureTable
                    results={results}
                    selectedDpcList={selectedDpcList}
                    calcTotalCase={calcTotalCase}
                  />
                )}
              </FacilityTabPanel>

              <FacilityTabPanel currentTab="map" tabValue={tabValue}>
                <FacilityMapView
                  ref={mapRef}
                  results={results ?? []}
                  selectedDpcList={selectedDpcList}
                  calcTotalCase={calcTotalCase}
                  onOpen={onSisetuClick}
                  onClose={closeSisetuDialog}
                />
              </FacilityTabPanel>
            </Box>

            <Stack
              direction="row"
              justifyContent="space-between"
              padding={1}
              sx={{ borderTop: 1, borderColor: 'divider' }}
            >
              <ValueSpan
                text={`${numberFormat(results?.length ?? 0)}`}
                suffix="施設"
                stackProps={{ paddingX: 1 }}
              />
              <Box>
                <Button
                  variant="contained"
                  disabled={!results || results.length < 1}
                  onClick={onDownload}
                >
                  ダウンロード
                </Button>
              </Box>
            </Stack>
          </Stack>
        </ColumnSplitter>
      </Stack>

      {sisetuCode && (
        <SisetuPreviewDialog
          sisetuList={sisetuList}
          sisetuCode={sisetuCode}
          onClose={closeSisetuDialog}
        />
      )}
    </Paper>
  )
}
